import { SpinnerMark } from "../atoms/spinner.tsx";
import * as React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@workspace/ui/lib/utils";

const dataGridVariants = cva(
    "ag-theme-quartz w-full",
    {
        variants: {
            size: {
                sm: "h-[300px]",
                md: "h-[400px]",
                lg: "h-[600px]",
                xl: "h-[800px]",
                full: "h-full",
            },
        },
        defaultVariants: {
            size: "md",
        },
    }
);

export interface DataGridProps
    extends React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof dataGridVariants> {
    data?: Array<Record<string, unknown>>;
    columnDefs?: Array<{ headerName?: string; field: string }>;
    onRowClick?: (event: Record<string, unknown>) => void;
    loading?: boolean;
}

export const DataGrid = React.forwardRef<HTMLDivElement, DataGridProps>(
    ({
        className,
        size,
        data = [],
        columnDefs = [],
        onRowClick,
        loading = false,
        ...props
    }, ref) => {
        // This is a placeholder implementation
        // In a real application, you'd use AG Grid or similar
        return (
            <div
                ref={ref}
                className={cn(dataGridVariants({ size }), className)}
                {...props}
            >
                <div className="border rounded-lg overflow-hidden">
                    {loading ? (
                        <div className="flex items-center justify-center h-32">
                            <SpinnerMark size={32} />
                        </div>
                    ) : (
                        <table className="w-full">
                            <thead className="bg-muted">
                                <tr>
                                    {columnDefs.map((col, index) => (
                                        <th key={index} className="p-3 text-left font-medium">
                                            {col.headerName || col.field}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {data.length === 0 ? (
                                    <tr>
                                        <td
                                            colSpan={columnDefs.length}
                                            className="p-8 text-center text-muted-foreground"
                                        >
                                            No data available
                                        </td>
                                    </tr>
                                ) : (
                                    data.map((row, rowIndex) => (
                                        <tr
                                            key={rowIndex}
                                            className="border-t hover:bg-muted/50 cursor-pointer"
                                            onClick={() => onRowClick?.(row)}
                                        >
                                            {columnDefs.map((col, colIndex) => (
                                                <td key={colIndex} className="p-3">
                                                    {String(row[col.field] ?? "")}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    )}
                </div>
            </div>
        );
    }
);

DataGrid.displayName = "DataGrid";
